import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
    useTours,
    useSiteSettings,
    useSaveSettings,
    formatPrice,
} from "@/lib/siteData";
import { Card, Field, SaveBtn, Ok, Err } from "./adminUI";
import CouponsAdmin from "./CouponsAdmin";
import BookingRulesAdmin from "./BookingRulesAdmin";
import SpecialPricesAdmin from "./SpecialPricesAdmin";
import { discountPct } from "@/lib/booking";

function TourPriceRow({ tour }) {
    const [price, setPrice] = useState(tour.price ?? "");
    const [promo, setPromo] = useState(tour.promo_price ?? "");
    const [starts, setStarts] = useState(tour.promo_starts ?? "");
    const [ends, setEnds] = useState(tour.promo_ends ?? "");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        setPrice(tour.price ?? "");
        setPromo(tour.promo_price ?? "");
    }, [tour.id, tour.price, tour.promo_price]);

    const save = async () => {
        setSaving(true);
        setErr("");
        const { error } = await supabase
            .from("tours")
            .update({
                price: price === "" ? null : Number(price),
                promo_price: promo === "" ? null : Number(promo),
                ...(tour.promo_starts !== undefined && { promo_starts: starts || null, promo_ends: ends || null }),
            })
            .eq("id", tour.id);
        setSaving(false);
        if (error) setErr("Erro: " + error.message);
        else {
            setMsg("Salvo!");
            setTimeout(() => setMsg(null), 2500);
        }
    };

    return (
        <div
            data-testid={`price-row-${tour.id}`}
            className="grid grid-cols-1 items-end gap-4 border-t border-ink/10 pt-4 sm:grid-cols-4"
        >
            <div className="sm:col-span-1">
                <p className="font-display text-sm font-extrabold text-ink">
                    {tour.title}
                </p>
                <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.2em] text-dim">
                    Atual: {formatPrice(tour.price) ?? "não exibido"}
                    {tour.promo_price
                        ? ` • promo ${formatPrice(tour.promo_price)} (-${discountPct(tour.price, tour.promo_price)}%)`
                        : ""}
                </p>
            </div>
            <Field label="Preço (R$)" type="number" value={price} onChange={setPrice} testid={`price-input-${tour.id}`} />
            <Field label="Promocional (R$)" type="number" value={promo} onChange={setPromo} testid={`price-promo-input-${tour.id}`} />
            {tour.promo_starts !== undefined && <>
                <Field label="Promoção a partir de" type="date" value={starts} onChange={setStarts} testid={`price-promo-start-${tour.id}`} />
                <Field label="Promoção até" type="date" value={ends} onChange={setEnds} testid={`price-promo-end-${tour.id}`} />
            </>}
            <div className="flex items-center justify-end gap-3">
                <Ok msg={msg} />
                <SaveBtn onClick={save} saving={saving} testid={`price-save-${tour.id}`} />
            </div>
        </div>
    );
}

export default function PricesAdmin() {
    const settings = useSiteSettings();
    const tours = useTours(true);
    const saveSettings = useSaveSettings();
    const [heroPrice, setHeroPrice] = useState(settings.hero_price);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        setHeroPrice(settings.hero_price);
    }, [settings.hero_price]);

    const saveHero = async () => {
        setSaving(true);
        setErr("");
        try {
            await saveSettings({ hero_price: heroPrice });
            setMsg("Salvo!");
            setTimeout(() => setMsg(null), 2500);
        } catch (ex) {
            setErr("Erro: " + ex.message);
        }
        setSaving(false);
    };

    return (
        <div className="space-y-8">
            <h1 className="font-display text-2xl font-black uppercase sm:text-3xl">
                Preços
            </h1>
            <Card className="space-y-4">
                <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                    Destaque do topo (hero) e letreiro
                </p>
                <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-3">
                    <Field
                        label="Preço exibido (ex.: R$150)"
                        value={heroPrice}
                        onChange={setHeroPrice}
                        testid="hero-price-input"
                    />
                    <div className="flex items-center justify-end gap-3 sm:col-span-2">
                        <Ok msg={msg} />
                        <Err msg={err} />
                        <SaveBtn onClick={saveHero} saving={saving} testid="hero-price-save" />
                    </div>
                </div>
            </Card>
            <Card className="space-y-4">
                <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                    Preços por passeio
                </p>
                {tours.map((t) => (
                    <TourPriceRow key={t.id} tour={t} />
                ))}
            </Card>
            <BookingRulesAdmin />
            <SpecialPricesAdmin />
            <CouponsAdmin />
        </div>
    );
}
